import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "@/hooks/useAuth";
import ReportProgressTimeline from "@/components/kalki/ReportProgressTimeline";
import LifeReportGenerationPopup from "@/components/kalki/LifeReportGenerationPopup";
import useLifeReportJob from "@/hooks/useLifeReportJob";
import "./LifeReport.css";

const FOCUS_OPTIONS = ["Career", "Money", "Love", "Family", "Purpose", "Spiritual"];

export default function LifeReport() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [balance, setBalance] = useState(0);
  // v0.6 Phase B — 500-credit eligibility (populated by /report/eligibility)
  const [eligibility, setEligibility] = useState(null);
  const [birth, setBirth] = useState({ date: "", time: "", city: "", country: "", latitude: null, longitude: null, timezone: 5.5, time_source: "exact" });
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ year: "", description: "" });
  const [focus, setFocus] = useState([]);
  const [questions, setQuestions] = useState(["", "", ""]);
  // v0.6 Phase B — 10-step extended state
  const [identity, setIdentity] = useState({ name: "", gender: "", current_city: "" });
  const [timeReliability, setTimeReliability] = useState("unknown");
  const [patterns, setPatterns] = useState([]);
  const [newPattern, setNewPattern] = useState("");
  const [career, setCareer] = useState({ current_work: "", career_goal: "", what_next: "" });
  const [rels, setRels] = useState({ status: "", family_priorities: "", main_question: "" });
  const [money, setMoney] = useState({ financial_concern: "", financial_priorities: "", wellbeing_notes: "" });
  const [prefs, setPrefs] = useState({ language: "en", format: "pdf", open_with_mantra: true, spiritual_lessons: true });
  const [consents, setConsents] = useState({ chart_mine: false, tendencies: false, data_use: false });
  const [starting, setStarting] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ stage: "idle", pct: 0, note: "" });
  // §B2 Mobile — track async job status + stages_completed so the timeline
  // UI can mark every stage that actually ran, even on fast pipelines.
  const [jobStatus, setJobStatus] = useState(null);
  const [stagesCompleted, setStagesCompleted] = useState([]);
  // v0.6 Phase E — backend-timestamp real timer (survives refresh).
  const [elapsedSeconds, setElapsedSeconds] = useState(null);
  const [targetSeconds, setTargetSeconds] = useState(600);
  const [report, setReport] = useState(null); // { id }
  // KALKI 7.0 — immediate popup + durable job polling.
  const [popupOpen, setPopupOpen] = useState(false);
  const lrJob = useLifeReportJob();
  // P0 Idempotency (Oct 2026): tie the idempotency key to the draft so a
  // page refresh does NOT generate a new key. If the user double-clicks or
  // their phone reconnects mid-generate, server-side idempotency returns the
  // same report — never a second 500-credit debit. Falls back to a session
  // key if no draft exists yet (edge case).
  const idemKey = useMemo(() => {
    if (draftId) return `lr-draft-${draftId}`;
    return `lr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }, [draftId]);

  // Redirect unauthenticated → login
  useEffect(() => {
    if (loading) return;
    if (!user) {
      sessionStorage.setItem("kalki_intent", "LIFE_REPORT");
      login();
    }
  }, [loading, user, login]);

  // Load existing draft + balance + eligibility
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // v0.6 Phase B — eligibility gate first. If ineligible, bounce to Wallet.
        try {
          const { data: elig } = await api.get("/report/eligibility");
          setEligibility(elig);
          if (!elig.onboarded) {
            toast.error("Please finish your 5-step onboarding first.");
            nav("/onboarding/your-beginning", { replace: true });
            return;
          }
          if (!elig.eligible) {
            toast.error(`You need ${elig.needed} more Credits to generate a Life Report.`);
            // Open Wallet (via same event used by 402 interceptor)
            window.dispatchEvent(new CustomEvent("kalki:credits-topup-needed",
              { detail: { balance: elig.balance, needed: elig.needed,
                          message: "A Life Report costs 500 credits. Add credits to continue." } }));
            nav("/app", { replace: true });
            return;
          }
        } catch (_) { /* non-blocking — allow start to proceed */ }

        const { data } = await api.post("/report/start");
        setDraftId(data.draft_id);
        setBalance(data.balance || 0);
        setStep(Math.max(1, Math.min(10, data.step || 1)));
        if (data.profile_birth) {
          setBirth((b) => ({ ...b, ...data.profile_birth }));
        }
        if (data.life_events?.length) setEvents(data.life_events);
        if (data.focus_areas?.length) setFocus(data.focus_areas);
        if (data.questions?.length) setQuestions([...data.questions, "", "", ""].slice(0, 3));
        // v0.6 Phase B — hydrate extended intake from server prefill
        if (data.identity) setIdentity((prev) => ({ ...prev, ...data.identity }));
        if (data.time_reliability) setTimeReliability(data.time_reliability);
        if (Array.isArray(data.life_patterns)) setPatterns(data.life_patterns);
        if (data.career) setCareer((prev) => ({ ...prev, ...data.career }));
        if (data.relationships) setRels((prev) => ({ ...prev, ...data.relationships }));
        if (data.money_wellbeing) setMoney((prev) => ({ ...prev, ...data.money_wellbeing }));
        if (data.preferences) setPrefs((prev) => ({ ...prev, ...data.preferences }));
        if (data.consents) setConsents((prev) => ({ ...prev, ...data.consents }));

        // v0.6 Phase E — resume a live report job on refresh so the real
        // timer picks up from the backend timestamp and the user never sees
        // a reset timeline.
        try {
          const jobs = await api.get("/reports/jobs?limit=1");
          const latest = (jobs?.data?.items || [])[0];
          const TERM = new Set(["COMPLETED", "FAILED", "REFUNDED"]);
          if (latest && !TERM.has(latest.status)) {
            // There IS an in-flight job — jump straight to step 10 and
            // resume polling.
            setStep(10);
            setGenerating(true);
            setJobStatus(latest.status);
            setStagesCompleted(Array.isArray(latest.stages_completed) ? latest.stages_completed : []);
            if (typeof latest.elapsed_seconds === "number") setElapsedSeconds(latest.elapsed_seconds);
            if (typeof latest.target_seconds === "number") setTargetSeconds(latest.target_seconds);
            setProgress({ stage: (latest.status || "").toLowerCase(), pct: latest.progress || 0, note: latest.stage || "" });
            // Kick a lightweight poll loop that mirrors generate()'s poller.
            (async () => {
              let doneLocal = false;
              while (!doneLocal) {
                await new Promise((r) => setTimeout(r, 1500));
                try {
                  const j = await api.get(`/reports/jobs/${latest.job_id}`);
                  const d = j.data;
                  setJobStatus(d.status);
                  setStagesCompleted(d.stages_completed || []);
                  if (typeof d.elapsed_seconds === "number") setElapsedSeconds(d.elapsed_seconds);
                  if (typeof d.target_seconds === "number") setTargetSeconds(d.target_seconds);
                  setProgress({ stage: (d.status || "").toLowerCase(), pct: d.progress || 0, note: d.stage || "" });
                  if (TERM.has(d.status)) {
                    doneLocal = true;
                    if (d.status === "COMPLETED" && d.report_id) {
                      setReport({ id: d.report_id, karna: false });
                      try { const p = await api.get("/profile"); setBalance(p.data?.credits ?? balance); } catch (_) {}
                    }
                    setGenerating(false);
                  }
                } catch (_) { /* keep polling */ }
              }
            })();
          }
        } catch (_) { /* non-blocking */ }
      } catch (e) {
        toast.error("Could not start Life Report. Please try again.");
      } finally {
        setStarting(false);
      }
    })();
  }, [user, nav]);

  const saveDraft = async (patch) => {
    if (!draftId) return;
    try { await api.patch(`/report/draft/${draftId}`, patch); } catch (e) { /* soft fail */ }
  };

  const next = async (patch) => {
    await saveDraft({ ...patch, step: step + 1 });
    setStep(step + 1);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const addEvent = () => {
    const y = parseInt(newEvent.year, 10);
    if (!y || !newEvent.description) return;
    const ev = { year: y, event_type: "life_event", description: newEvent.description };
    setEvents((e) => [...e, ev]);
    setNewEvent({ year: "", description: "" });
  };

  const toggleFocus = (f) => setFocus((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : (cur.length >= 3 ? cur : [...cur, f]));

  const canGenerate = balance >= 500;

  // KALKI 7.0 — rewrite of generate(): popup opens SYNCHRONOUSLY, then the
  // async job starts.  No sync fallback, no credits-before-success risk.
  const generate = async () => {
    if (lrJob.status && !lrJob.isTerminal) {
      // Idempotent — if a job is already running, just show the popup.
      setPopupOpen(true);
      return;
    }
    if (balance < 500) {
      toast.error("You need 500 Credits to generate your Life Report.");
      return;
    }
    // §2 — popup must appear WITHIN ~200 ms.  Open first, network second.
    setPopupOpen(true);
    try {
      // Make sure the latest intake is on the server before firing the job.
      await saveDraft({
        birth: birth.date ? birth : null,
        life_events: events,
        focus_areas: focus,
        questions: questions.filter((q) => q.trim()),
      });
      await lrJob.start({ idempotency_key: idemKey, draft_id: draftId });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.code : null;
      if (code === "INSUFFICIENT_CREDITS") {
        toast.error(detail.message || "Not enough Credits.");
        setPopupOpen(false);
        return;
      }
      if (code === "ONBOARDING_REQUIRED") {
        toast.error(detail.message || "Finish onboarding first.");
        setPopupOpen(false);
        nav("/onboarding/your-beginning");
        return;
      }
      if (code === "NO_DRAFT" || code === "NEEDS_INFORMATION") {
        toast.error(detail.message || "A few more details needed.");
        setPopupOpen(false);
        setStep(1);
        return;
      }
      toast.error("Could not start Life Report. Please try again.");
      setPopupOpen(false);
    }
  };

  // Mirror the durable job snapshot into the legacy progress state so the
  // existing step-10 timeline UI keeps working while the popup is in the
  // background.
  useEffect(() => {
    if (!lrJob.status) return;
    setGenerating(!lrJob.isTerminal);
    setJobStatus(lrJob.status);
    setStagesCompleted(lrJob.stagesCompleted || []);
    setElapsedSeconds(lrJob.elapsedSeconds);
    setTargetSeconds(lrJob.targetSeconds);
    setProgress({ stage: lrJob.status.toLowerCase(),
                  pct: lrJob.progress || 0,
                  note: lrJob.stageLabel || "" });
    if (lrJob.status === "COMPLETED" && lrJob.reportId) {
      setReport({ id: lrJob.reportId, karna: false });
      // Refresh balance exactly once from the server after capture.
      (async () => {
        try { const p = await api.get("/profile");
              setBalance(p.data?.credits ?? balance); } catch (_) {}
      })();
    }
  }, [lrJob.status, lrJob.progress, lrJob.reportId, lrJob.elapsedSeconds]);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [iosSaveHint, setIosSaveHint] = useState(false);
  const isIOS = useMemo(
    () => typeof window !== "undefined"
      && /iPad|iPhone|iPod/.test(navigator.userAgent)
      && !window.MSStream,
    []
  );

  // Universal delivery URLs. VIEW endpoint returns Content-Disposition: inline;
  // DOWNLOAD endpoint returns Content-Disposition: attachment. Same backend,
  // same stored PDF, cookie auth works on every device.
  const viewUrl = useMemo(() => {
    if (!report?.id) return null;
    return report.karna
      ? `${process.env.REACT_APP_BACKEND_URL}/api/report/karna/${report.id}/pdf`
      : `${process.env.REACT_APP_BACKEND_URL}/api/report/${report.id}/view`;
  }, [report]);
  const downloadUrl = useMemo(() => {
    if (!report?.id) return null;
    return report.karna
      ? `${process.env.REACT_APP_BACKEND_URL}/api/report/karna/${report.id}/pdf?download=1`
      : `${process.env.REACT_APP_BACKEND_URL}/api/report/${report.id}/download`;
  }, [report]);

  const openPdf = () => {
    if (!viewUrl) return;
    // Sprint 3 — Prompt E4: in-app PDF viewer. Avoids popup blockers that
    // broke the mobile flow in Monish's review.
    setPdfOpen(true);
  };

  const downloadPdf = () => {
    if (!downloadUrl) return;
    if (isIOS) {
      // iOS Safari ignores the <a download> attribute. The only reliable way
      // to deliver the PDF is to navigate to the view endpoint — Safari
      // opens its native PDF viewer where the user can tap Share → Save to
      // Files. Show a tiny coachmark so they know to tap Share.
      setIosSaveHint(true);
      // Fallback — if the user dismisses the hint, open the PDF directly.
      window.open(viewUrl, "_blank", "noopener");
      return;
    }
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.setAttribute("download", `KALKI_Life_Report_${report.id.slice(0, 8)}.pdf`);
    a.setAttribute("target", "_blank");
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // KALKI 7.0 — the premium generation popup lives at the top level so it
  // can render independently of the step wizard underneath it.  The job
  // keeps running even when the popup is dismissed into Background mode.
  const generationPopup = (
    <LifeReportGenerationPopup
      open={popupOpen}
      status={lrJob.status}
      stageLabel={lrJob.stageLabel}
      stagesCompleted={lrJob.stagesCompleted}
      remainingSeconds={lrJob.remainingSeconds}
      reportId={lrJob.reportId}
      error={lrJob.error}
      onStayAndWatch={() => {}}
      onBackground={() => setPopupOpen(false)}
      onClose={() => setPopupOpen(false)}
      onViewReport={() => {
        setPopupOpen(false);
        openPdf();
      }}
      onRetry={() => {
        // Clear the dead job and start fresh.
        lrJob.dismiss();
        setPopupOpen(false);
        // Fire again on the next tick so React can rebind the fresh snapshot.
        setTimeout(() => generate(), 50);
      }}
    />
  );

  if (loading || starting) {
    return (
      <div className="lr-shell"><div className="lr-topbar"><div className="lr-mark">KALKI</div></div>
        <div style={{textAlign:"center",marginTop:"35dvh",color:"#676773",fontSize:14,letterSpacing:".3em",textTransform:"uppercase"}}>Preparing your journey…</div>
      </div>);
  }

  const stepLabels = [
    "Personal Identity",
    "Birth Details",
    "Life Milestones",
    "Long-Term Patterns",
    "Career & Aspirations",
    "Relationships & Family",
    "Money & Wellbeing",
    "Three Important Questions",
    "Report Preferences",
    "Review & Generate",
  ];
  const TOTAL_STEPS = 10;
  const stepNum = String(step).padStart(2, "0");
  const dark = step >= 9;

  return (
    <>
      {generationPopup}
    <div className={`lr-shell ${dark ? "lr-dark" : ""}`} data-testid="life-report-shell">
      <div className="lr-topbar">
        <div className="lr-mark">KALKI</div>
        <div className="lr-step-pill" data-testid="lr-step-pill">{step} of {TOTAL_STEPS}</div>
        <button className="lr-close" onClick={() => nav("/app")} data-testid="lr-close">Close ×</button>
      </div>

      <div className="lr-progress" aria-hidden>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
          <div key={n} className={`seg ${n < step ? "done" : n === step ? "active" : ""}`} />
        ))}
      </div>

      <div className="lr-stage">
        <div className="lr-left">
          <div className="lr-step-num"><span className="step-n">{stepNum}</span> · {stepLabels[step-1]}</div>

          {step === 1 && (
            <>
              <h1 className="lr-headline">About <em>you.</em></h1>
              <div className="lr-krishna-say">Let me know who I am speaking with. These details are prefilled from your profile — edit anything that's not right.</div>
              <div className="lr-input-group">
                <input className="lr-input" placeholder="Full name" value={identity.name || ""}
                       onChange={(e) => setIdentity({ ...identity, name: e.target.value })} data-testid="lr-identity-name" />
                <div className="lr-row2">
                  <select className="lr-input" value={identity.gender || ""}
                          onChange={(e) => setIdentity({ ...identity, gender: e.target.value })} data-testid="lr-identity-gender">
                    <option value="">Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_say">Prefer not to say</option>
                  </select>
                  <input className="lr-input" placeholder="Current city" value={identity.current_city || ""}
                         onChange={(e) => setIdentity({ ...identity, current_city: e.target.value })} data-testid="lr-identity-city" />
                </div>
              </div>
              <div className="lr-actions">
                <button className="lr-primary" disabled={!identity.name || !identity.gender}
                        onClick={() => next({ identity })} data-testid="lr-step1-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="lr-headline">Your <em>birth.</em></h1>
              <div className="lr-krishna-say">Accurate birth timing shapes your chart. These are the five mandatory details — already saved from onboarding.</div>
              <div className="lr-input-group">
                <input className="lr-input" type="date" value={birth.date || ""} onChange={(e) => setBirth({ ...birth, date: e.target.value })} data-testid="lr-birth-date" />
                <input className="lr-input" type="time" value={birth.time || ""} onChange={(e) => setBirth({ ...birth, time: e.target.value })} data-testid="lr-birth-time" />
                <div className="lr-row2">
                  <input className="lr-input" placeholder="Birth city" value={birth.city || ""} onChange={(e) => setBirth({ ...birth, city: e.target.value })} data-testid="lr-birth-city" />
                  <input className="lr-input" placeholder="Country" value={birth.country || ""} onChange={(e) => setBirth({ ...birth, country: e.target.value })} data-testid="lr-birth-country" />
                </div>
                <select className="lr-input" value={timeReliability}
                        onChange={(e) => setTimeReliability(e.target.value)} data-testid="lr-time-reliability">
                  <option value="certificate">Birth certificate</option>
                  <option value="patri">Patri / horoscope</option>
                  <option value="memory">Family memory</option>
                  <option value="approximate">Approximate</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" disabled={!birth.date || !birth.time || !birth.city}
                        onClick={() => next({ birth, time_reliability: timeReliability })} data-testid="lr-step2-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="lr-headline">Moments that <em>shaped you.</em></h1>
              <div className="lr-krishna-say">Four to eight meaningful events help me match your chart to your real life.</div>
              <div className="lr-events" data-testid="lr-events-list">
                {events.map((ev, i) => (
                  <div key={i} className="lr-event"><span className="yr">{ev.year}</span><span className="desc">{ev.description || ev.event_type}</span><button className="rm" onClick={() => setEvents(events.filter((_, idx) => idx !== i))}>×</button></div>
                ))}
              </div>
              <div className="lr-input-group" style={{marginTop:16}}>
                <div className="lr-row2">
                  <input className="lr-input" placeholder="Year" type="number" value={newEvent.year} onChange={(e) => setNewEvent({ ...newEvent, year: e.target.value })} data-testid="lr-event-year" />
                  <input className="lr-input" placeholder="Started my business, got married…" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} data-testid="lr-event-desc" />
                </div>
                <button className="lr-ghost" onClick={addEvent} data-testid="lr-add-event">+ Add life moment</button>
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ life_events: events })} data-testid="lr-step3-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="lr-headline">Patterns in your <em>path.</em></h1>
              <div className="lr-krishna-say">Themes that keep returning — career effort, relationship transitions, relocation, growth. Add what resonates.</div>
              <div className="lr-events" data-testid="lr-patterns-list">
                {patterns.map((p, i) => (
                  <div key={i} className="lr-event"><span className="desc">{p}</span><button className="rm" onClick={() => setPatterns(patterns.filter((_, idx) => idx !== i))}>×</button></div>
                ))}
              </div>
              <div className="lr-input-group" style={{marginTop:16}}>
                <input className="lr-input" placeholder="e.g. Long periods of career effort before results"
                       value={newPattern} onChange={(e) => setNewPattern(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter' && newPattern.trim()) { setPatterns([...patterns, newPattern.trim()]); setNewPattern(""); } }}
                       data-testid="lr-pattern-input" />
                <button className="lr-ghost" disabled={!newPattern.trim()}
                        onClick={() => { setPatterns([...patterns, newPattern.trim()]); setNewPattern(""); }}
                        data-testid="lr-add-pattern">+ Add pattern</button>
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ life_patterns: patterns })} data-testid="lr-step4-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 5 && (
            <>
              <h1 className="lr-headline">Career & <em>what's next.</em></h1>
              <div className="lr-krishna-say">Where your effort is going — and where you want it to go.</div>
              <div className="lr-input-group">
                <input className="lr-input" placeholder="Current work or business" value={career.current_work || ""}
                       onChange={(e) => setCareer({ ...career, current_work: e.target.value })} data-testid="lr-career-current" />
                <input className="lr-input" placeholder="Main professional goal" value={career.career_goal || ""}
                       onChange={(e) => setCareer({ ...career, career_goal: e.target.value })} data-testid="lr-career-goal" />
                <input className="lr-input" placeholder="What you want next" value={career.what_next || ""}
                       onChange={(e) => setCareer({ ...career, what_next: e.target.value })} data-testid="lr-career-next" />
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ career })} data-testid="lr-step5-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 6 && (
            <>
              <h1 className="lr-headline">Relationships & <em>family.</em></h1>
              <div className="lr-krishna-say">Share only what you want to — nothing sensitive is required.</div>
              <div className="lr-input-group">
                <select className="lr-input" value={rels.status || ""}
                        onChange={(e) => setRels({ ...rels, status: e.target.value })} data-testid="lr-rels-status">
                  <option value="">Relationship status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                  <option value="separated">Separated</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_say">Prefer not to say</option>
                </select>
                <input className="lr-input" placeholder="Family priorities (parents, children, home…)"
                       value={rels.family_priorities || ""}
                       onChange={(e) => setRels({ ...rels, family_priorities: e.target.value })} data-testid="lr-rels-family" />
                <input className="lr-input" placeholder="Main relationship question (optional)"
                       value={rels.main_question || ""}
                       onChange={(e) => setRels({ ...rels, main_question: e.target.value })} data-testid="lr-rels-question" />
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ relationships: rels })} data-testid="lr-step6-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 7 && (
            <>
              <h1 className="lr-headline">Money & <em>wellbeing.</em></h1>
              <div className="lr-krishna-say">Broad strokes only. No medical or detailed financial information needed.</div>
              <div className="lr-input-group">
                <input className="lr-input" placeholder="Main financial concern" value={money.financial_concern || ""}
                       onChange={(e) => setMoney({ ...money, financial_concern: e.target.value })} data-testid="lr-money-concern" />
                <input className="lr-input" placeholder="Financial priorities" value={money.financial_priorities || ""}
                       onChange={(e) => setMoney({ ...money, financial_priorities: e.target.value })} data-testid="lr-money-priority" />
                <input className="lr-input" placeholder="Any wellbeing notes (optional)" value={money.wellbeing_notes || ""}
                       onChange={(e) => setMoney({ ...money, wellbeing_notes: e.target.value })} data-testid="lr-money-wellbeing" />
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ money_wellbeing: money })} data-testid="lr-step7-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 8 && (
            <>
              <h1 className="lr-headline">Three questions you want <em>answered.</em></h1>
              <div className="lr-krishna-say">Up to three. These shape the entire report.</div>
              <div className="lr-input-group">
                {[0,1,2].map((i) => (
                  <input key={i} className="lr-input" placeholder={i === 0 ? "e.g. When might my career improve?" : i === 1 ? "e.g. What should I focus on financially?" : "e.g. How can I approach my relationships wisely?"}
                         value={questions[i]} onChange={(e) => { const q = [...questions]; q[i] = e.target.value; setQuestions(q); }} data-testid={`lr-q-${i}`} />
                ))}
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ questions: questions.filter((q) => q.trim()) })} data-testid="lr-step8-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 9 && (
            <>
              <h1 className="lr-headline">Report <em>preferences.</em></h1>
              <div className="lr-krishna-say">Choose how your Life Report feels.</div>
              <div className="lr-input-group">
                <select className="lr-input" value={prefs.language}
                        onChange={(e) => setPrefs({ ...prefs, language: e.target.value })} data-testid="lr-pref-language">
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="gu">Gujarati</option>
                </select>
                <label className="lr-check" data-testid="lr-pref-mantra-wrap">
                  <input type="checkbox" checked={!!prefs.open_with_mantra}
                         onChange={(e) => setPrefs({ ...prefs, open_with_mantra: e.target.checked })} data-testid="lr-pref-mantra" />
                  <span>Open with "॥ ॐ गणेशाय नमः ॥"</span>
                </label>
                <label className="lr-check" data-testid="lr-pref-spiritual-wrap">
                  <input type="checkbox" checked={!!prefs.spiritual_lessons}
                         onChange={(e) => setPrefs({ ...prefs, spiritual_lessons: e.target.checked })} data-testid="lr-pref-spiritual" />
                  <span>Include Spiritual Life Lessons section</span>
                </label>
              </div>
              <div className="lr-actions">
                <button className="lr-ghost" onClick={back}>← Back</button>
                <button className="lr-primary" onClick={() => next({ preferences: prefs })} data-testid="lr-step9-continue">Continue →</button>
              </div>
            </>
          )}

          {step === 10 && !report && (
            <>
              <h1 className="lr-headline">Review & <em>generate.</em></h1>
              <div className="lr-krishna-say">A final look before we begin.</div>
              <div className="lr-review" data-testid="lr-review-summary" style={{marginTop:12,padding:"14px 16px",borderRadius:14,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.09)",color:"#F6F1FF",fontSize:13,lineHeight:1.6}}>
                <div><strong>Name:</strong> {identity.name || "—"} · <strong>Gender:</strong> {identity.gender || "—"}</div>
                <div><strong>Birth:</strong> {birth.date || "—"} {birth.time} · {birth.city}{birth.country ? ", " + birth.country : ""}</div>
                <div><strong>Events:</strong> {events.length || 0}</div>
                <div><strong>Questions:</strong> {questions.filter((q) => q.trim()).length || 0}</div>
                <div><strong>Language:</strong> {{en:"English",hi:"Hindi",gu:"Gujarati"}[prefs.language]} · <strong>Format:</strong> PDF</div>
              </div>
              {!canGenerate ? (
                <div className="lr-insufficient" data-testid="lr-insufficient" style={{marginTop:16}}>
                  <div style={{fontSize:11,letterSpacing:".3em",color:"#C7C7D1",textTransform:"uppercase"}}>Current balance</div>
                  <div className="bal">✦ {balance}</div>
                  <div className="need">You need ✦ {500 - balance} more Credits.</div>
                  <div className="lr-actions" style={{marginTop:20}}>
                    <button className="lr-primary" onClick={() => {
                      window.dispatchEvent(new CustomEvent("kalki:credits-topup-needed",
                        { detail: { balance, needed: 500 - balance,
                                    message: "A Life Report costs 500 credits. Add credits to continue." } }));
                      nav("/app");
                    }} data-testid="lr-recharge">Open Wallet</button>
                    <button className="lr-ghost" onClick={() => nav("/app")}>Not now</button>
                  </div>
                </div>
              ) : (
                <div className="lr-ready-card" data-testid="lr-ready-card" style={{marginTop:18}}>
                  <div className="eye">✦ Please confirm the three agreements</div>
                  <div className="lr-input-group" style={{textAlign:"left",marginTop:14}}>
                    <label className="lr-check" data-testid="lr-consent-chart-wrap">
                      <input type="checkbox" checked={!!consents.chart_mine}
                             onChange={(e) => setConsents({ ...consents, chart_mine: e.target.checked })} data-testid="lr-consent-chart" />
                      <span>This chart is mine, or I have permission to submit these birth details.</span>
                    </label>
                    <label className="lr-check" data-testid="lr-consent-tendencies-wrap">
                      <input type="checkbox" checked={!!consents.tendencies}
                             onChange={(e) => setConsents({ ...consents, tendencies: e.target.checked })} data-testid="lr-consent-tendencies" />
                      <span>I understand traditional astrology describes possible tendencies and timing — not guaranteed fate, nor medical, legal or financial advice.</span>
                    </label>
                    <label className="lr-check" data-testid="lr-consent-data-wrap">
                      <input type="checkbox" checked={!!consents.data_use}
                             onChange={(e) => setConsents({ ...consents, data_use: e.target.checked })} data-testid="lr-consent-data" />
                      <span>I authorize KALKI to use the submitted information to generate this report.</span>
                    </label>
                  </div>
                  <div className="price" style={{marginTop:16}}>
                    <span className="amt">✦ 500</span>
                    <span className="lbl">KALKI Credits</span>
                  </div>
                  <div className="lr-balance-preview" data-testid="lr-balance-preview" style={{marginTop:18,padding:"14px 18px",borderRadius:14,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"rgba(230,220,255,0.6)"}}>Current balance</div>
                      <div style={{fontFamily:"Cormorant Garamond, serif",fontSize:22,color:"#F6F1FF"}}>✦ {balance}</div>
                    </div>
                    <div style={{color:"rgba(230,220,255,0.4)",fontSize:16}}>→</div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"rgba(230,220,255,0.6)"}}>After generation</div>
                      <div style={{fontFamily:"Cormorant Garamond, serif",fontSize:22,background:"linear-gradient(135deg,#CFA8FF,#FF9BD2)",WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent"}}>✦ {Math.max(0, balance - 500)}</div>
                    </div>
                  </div>
                  <div className="lr-actions" style={{marginTop:20}}>
                    <button className="lr-primary"
                            onClick={async () => { await saveDraft({ consents }); generate(); }}
                            disabled={generating || !consents.chart_mine || !consents.tendencies || !consents.data_use}
                            data-testid="lr-generate">
                      {generating ? "Generating…" : "Confirm & Generate My Life Report"}
                    </button>
                    <button className="lr-ghost" onClick={back} disabled={generating}>Review details</button>
                  </div>
                  {generating && (
                    <div className="lr-progress" data-testid="lr-progress" style={{marginTop: 24, maxWidth: 480, margin: "24px auto 0"}}>
                      <ReportProgressTimeline
                        jobStatus={jobStatus}
                        legacyStage={progress.stage}
                        stagesCompleted={stagesCompleted}
                        done={!!report}
                        failed={progress.stage === "failed"}
                        elapsedSeconds={elapsedSeconds}
                        targetSeconds={targetSeconds}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 10 && report && (
            <>
              <h1 className="lr-headline">✓ Report <em>ready.</em></h1>
              <div className="lr-krishna-say">Your Life Report has been generated. Open it right here or download the PDF to keep.</div>
              <div className="lr-actions" style={{marginTop:28}}>
                <button className="lr-primary" onClick={openPdf} data-testid="lr-open-pdf">Open Report</button>
                <button className="lr-ghost" onClick={downloadPdf} data-testid="lr-download-pdf">Download PDF</button>
                <button
                  className="lr-ghost"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/report/${report.id}/discuss`, {
                        section_key: "s11_questions",
                        question: "Walk me through my Life Report — start with my three questions.",
                      });
                      sessionStorage.setItem("kalki_discuss_prompt", data.prompt);
                      nav(`/app/chat?prompt=${encodeURIComponent("Walk me through my Life Report — start with my three questions.")}&report=${report.id}`);
                    } catch (e) {
                      toast.error("Could not open the discussion. Try again.");
                    }
                  }}
                  data-testid="lr-discuss"
                >Ask KALKI about this</button>
                <button className="lr-ghost" onClick={() => nav("/app")}>Back to KALKI</button>
              </div>
              <p style={{color:"#C7C7D1",marginTop:28,fontSize:13,maxWidth:"46ch",lineHeight:1.6}}>Your report is also saved in <strong>Me → My Life Reports</strong> for future access — no charge to reopen.</p>
            </>
          )}
        </div>

      </div>

      {/* Sprint 3 — Prompt E4: in-app PDF viewer (no popup blockers) */}
      {pdfOpen && viewUrl && (
        <div
          data-testid="lr-pdf-viewer"
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(10,6,25,0.92)", backdropFilter: "blur(12px)",
            display: "flex", flexDirection: "column",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)",
            color: "#F6F1FF",
          }}>
            <div style={{fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "#CFA8FF"}}>
              KALKI · Life Report
            </div>
            <div style={{display: "flex", gap: 10}}>
              <button
                data-testid="lr-pdf-download"
                onClick={downloadPdf}
                style={{
                  background: "linear-gradient(135deg,#5D00FF,#FF2E9A)", color: "white",
                  border: 0, padding: "8px 16px", borderRadius: 999, cursor: "pointer",
                  fontSize: 12, letterSpacing: "0.2em",
                }}
              >DOWNLOAD</button>
              <button
                data-testid="lr-pdf-close"
                onClick={() => setPdfOpen(false)}
                style={{
                  background: "rgba(255,255,255,0.1)", color: "white",
                  border: "1px solid rgba(255,255,255,0.14)",
                  padding: "8px 14px", borderRadius: 999, cursor: "pointer",
                  fontSize: 14, lineHeight: 1,
                }}
                aria-label="Close"
              >✕</button>
            </div>
          </div>
          <iframe
            src={viewUrl}
            title="Life Report"
            style={{flex: 1, border: 0, background: "white", width: "100%"}}
          />
        </div>
      )}

      {/* iOS save-to-files coachmark — Safari ignores <a download> so we
          explain the Share flow once instead of silently failing. */}
      {iosSaveHint && (
        <div
          data-testid="ios-save-hint"
          onClick={() => setIosSaveHint(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1100,
            background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
          role="dialog" aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#F8F3EC", color: "#1C1C1C",
              borderTopLeftRadius: 24, borderTopRightRadius: 24,
              padding: "22px 22px calc(22px + env(safe-area-inset-bottom))",
              maxWidth: 460, width: "100%",
              boxShadow: "0 -24px 60px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{fontSize: 10, letterSpacing: "0.32em", color: "#5D00FF", marginBottom: 8}}>
              SAVE TO FILES
            </div>
            <h3 style={{fontSize: 20, margin: "0 0 10px 0", fontWeight: 500}}>
              Save your Life Report to Files
            </h3>
            <p style={{fontSize: 14, lineHeight: 1.5, color: "#434353", margin: "0 0 16px 0"}}>
              1. Your PDF just opened in Safari.<br/>
              2. Tap the <strong>Share</strong> icon (square with arrow).<br/>
              3. Choose <strong>Save to Files</strong> → <strong>On My iPhone</strong>.
            </p>
            <button
              data-testid="ios-save-hint-close"
              onClick={() => setIosSaveHint(false)}
              style={{
                width: "100%", background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                color: "white", border: 0, padding: "14px 18px", borderRadius: 999,
                fontSize: 13, letterSpacing: "0.2em", fontWeight: 500, cursor: "pointer",
              }}
            >GOT IT</button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
